import { Entity } from './base-raptor-models';
import { Part } from './part.model';
import { Labor } from './labor.model';
import { Package } from './package.model';

/**
 * Represents the type of product in the catalog.
 * Maps to the backend ProductType enum stored as string in the database.
 */
export enum ProductType {
    /** A physical part or component (e.g., tire, brake pad, filter) */
    Part = 'Part',
    /** A service or labor item (e.g., oil change, wheel alignment, inspection) */
    Labor = 'Labor',
    /** A bundle of parts and/or labor items sold together as a package */
    Package = 'Package',
}

/**
 * Product represents a sellable catalog item that can be a Part, Labor, or Package.
 * Acts as an abstraction layer between federation catalogs (GTX, TM, DVI) and
 * operational entities (Part, Labor, Package). This design enables:
 * - Semantic correctness: Services aren't parts, packages aren't parts
 * - Multi-federation support: Each system can return appropriately typed items
 * - Simplified references: AppointmentAddOn uses single productId instead of three optional fields
 * - Catalog price management: Separation between catalog price and configured price
 */
export interface Product extends Entity {
    /** Display name for this product */
    name: string;

    /** Internal product number or SKU (optional) */
    number?: string;

    /** Detailed description of this product (optional) */
    description?: string;

    /**
     * External catalog code from the federation system.
     * Examples: GTX ProductCode, TM ItemCode, DVI PartNumber.
     * Used for catalog sync and price updates.
     */
    externalCode: string;

    /**
     * Current catalog price from the external system.
     * This is not a snapshot - it reflects current pricing and can be updated during catalog sync.
     */
    catalogPrice: number;

    /**
     * The type of this product (Part, Labor, or Package).
     */
    type: ProductType;

    /**
     * Foreign key to the Part entity.
     * Only set when type = ProductType.Part.
     * Mutually exclusive with laborId and packageId.
     */
    partId?: string;

    /**
     * Navigation property to the Part entity.
     * Provides access to physical part details when type = ProductType.Part.
     */
    part?: Part;

    /**
     * Foreign key to the Labor entity.
     * Only set when type = ProductType.Labor.
     * Mutually exclusive with partId and packageId.
     */
    laborId?: string;

    /**
     * Navigation property to the Labor entity.
     * Provides access to service/labor details when type = ProductType.Labor.
     */
    labor?: Labor;

    /**
     * Foreign key to the Package entity.
     * Only set when type = ProductType.Package.
     * Mutually exclusive with partId and laborId.
     */
    packageId?: string;

    /**
     * Navigation property to the Package entity.
     * Provides access to package composition when type = ProductType.Package.
     */
    package?: Package;
}

export const createMockProduct = (overrides?: Partial<Product>): Product => {
    return {
        id: 'product-1',
        tenantId: 'tenant-123',
        branchId: 'branch-456',
        name: 'Premium Oil Change',
        number: 'POC-001',
        description: 'Full synthetic oil change',
        externalCode: 'POC001',
        catalogPrice: 49.99,
        type: ProductType.Labor,
        createdDate: new Date(),
        modifiedDate: new Date(),
        ...overrides,
    };
};
